/* Created by Interactive 63 - https://interactive63.com */
/**
 * Global keyboard shortcuts. Shortcuts are ignored while focus is in a text field so the
 * browser's own text editing keeps working.
 */
import { adjacentKeyframeTime } from '../core/timeline/keyframes';
import type { PlaybackController } from './playback';
import { copySelection, duplicateAtPlayhead } from './clipboard';
import { clearSelection } from './store/actions';
import { keyAllAtPlayhead } from './store/property-actions';
import {
  deleteSelectedLayers,
  duplicateSelectedLayers,
  groupSelectedLayers,
  reportArrange,
  ungroupSelectedLayers,
} from './store/layer-actions';
import { deleteKeyframes, selectAllKeyframesOnSelected } from './store/timeline-actions';
import { deleteSelectedStops, selectedStops } from './store/stop-actions';
import { addTextAndEdit } from './store/text-actions';
import { toast } from './ui/toast';
import type { Store } from './store/store';

const FRAME_MS = 1000 / 60;
const NUDGE_MS = 100;

/** Whether an event target is a control where typing, and the browser's own undo, applies. */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

/** Input types where Space is a typed character; the rest (checkbox, radio, range, button) leave it to playback. */
const TEXT_INPUT_TYPES = new Set([
  'text',
  'search',
  'url',
  'tel',
  'email',
  'password',
  'number',
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
  '',
]);

/** Whether Space would be typed or used by the control, so it must not toggle playback. */
function spaceBelongsToTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(target.type);
  return (
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

/** Handles Delete, T and Ctrl + C, D and A for keyframes and layers. Returns whether it acted. */
function handleEditKey(event: KeyboardEvent, store: Store, playback: PlaybackController): boolean {
  const { editor } = store.getState();
  if (event.key === 'Delete' || event.key === 'Backspace') {
    if (editor.selection.keyframeIds.length > 0) {
      deleteKeyframes(store, editor.selection.keyframeIds);
      return true;
    }
    // Backspace is too easy to hit by accident to delete artwork; only Delete does.
    if (event.key === 'Delete' && editor.selection.elementIds.length > 0) {
      if (selectedStops(store).length > 0) {
        reportArrange(deleteSelectedStops(store));
        return true;
      }
      if (deleteSelectedLayers(store) > 0) toast.info('Layer deleted. Undo brings it back.');
      return true;
    }
    return false;
  }
  if (event.key.toLowerCase() === 't' && !event.ctrlKey && !event.metaKey && !event.altKey) {
    addTextAndEdit(store);
    return true;
  }
  if (!(event.ctrlKey || event.metaKey) || event.altKey) return false;
  // Ctrl + G groups the selected layers and Ctrl + Shift + G dissolves the selected groups.
  if (event.key.toLowerCase() === 'g') {
    reportArrange(event.shiftKey ? ungroupSelectedLayers(store) : groupSelectedLayers(store));
    return true;
  }
  if (event.shiftKey) return false;
  switch (event.key.toLowerCase()) {
    case 'c':
      // The browser's own copy event also fires; this keeps an in-memory copy regardless.
      copySelection(store);
      return false;
    case 'd':
      if (editor.selection.keyframeIds.length > 0) duplicateAtPlayhead(store, playback.currentTime);
      else duplicateSelectedLayers(store);
      return true;
    case 'a':
      selectAllKeyframesOnSelected(store);
      return true;
    default:
      return false;
  }
}

/** Handles Q, W and E: previous keyframe, key at the playhead, next keyframe. Returns whether it acted. */
function handleKeyframeKey(
  event: KeyboardEvent,
  store: Store,
  playback: PlaybackController,
): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  const { editor, project } = store.getState();
  const ids = editor.selection.elementIds;
  const key = event.key.toLowerCase();
  if (key === 'w') {
    if (ids.length > 0) keyAllAtPlayhead(store, ids, playback.currentTime);
    return true;
  }
  if (key === 'q' || key === 'e') {
    const target = adjacentKeyframeTime(
      project,
      ids,
      playback.currentTime,
      key === 'q' ? 'previous' : 'next',
    );
    if (target !== undefined) playback.seek(target);
    return true;
  }
  return false;
}

/** Handles the playback keys: Space, Home, End and the arrow keys. Returns whether it acted. */
function handlePlaybackKey(event: KeyboardEvent, playback: PlaybackController): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  switch (event.key) {
    case ' ':
      if (spaceBelongsToTarget(event.target)) return false;
      if (playback.isPlaying) playback.pause();
      else playback.play();
      return true;
    case 'Home':
      playback.seek(0);
      return true;
    case 'End':
      playback.seek(Number.POSITIVE_INFINITY);
      return true;
    case 'ArrowLeft':
      playback.seek(playback.currentTime - (event.shiftKey ? NUDGE_MS : FRAME_MS));
      return true;
    case 'ArrowRight':
      playback.seek(playback.currentTime + (event.shiftKey ? NUDGE_MS : FRAME_MS));
      return true;
    default:
      return false;
  }
}

/** Registers the global shortcuts and returns a function that removes them. */
export function registerShortcuts(
  store: Store,
  playback?: PlaybackController,
  onHelp?: () => void,
): () => void {
  /** Handles undo, redo and playback keys. */
  const onKeyDown = (event: KeyboardEvent): void => {
    // Components such as the layers tree consume their own keys first.
    if (event.defaultPrevented) return;
    // Space plays and pauses wherever focus is, including on a button, unless it is being typed.
    if (playback && event.key === ' ' && !spaceBelongsToTarget(event.target)) {
      event.preventDefault();
      if (!event.repeat) handlePlaybackKey(event, playback);
      return;
    }
    if (isTextEntryTarget(event.target)) {
      // Escape leaves a field; every other key belongs to it.
      if (event.key === 'Escape' && event.target instanceof HTMLElement) event.target.blur();
      return;
    }
    if (event.key === 'Escape') {
      clearSelection(store);
      return;
    }
    // H is the easy key; ? is kept because it is the common one, but it needs Shift + /.
    const asksForHelp = event.key === '?' || event.key.toLowerCase() === 'h';
    if (asksForHelp && onHelp && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      onHelp();
      return;
    }
    if (playback && handleEditKey(event, store, playback)) {
      event.preventDefault();
      return;
    }
    if (
      playback &&
      (handleKeyframeKey(event, store, playback) || handlePlaybackKey(event, playback))
    ) {
      event.preventDefault();
      return;
    }
    if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
    const key = event.key.toLowerCase();
    if (key === 'z' && !event.shiftKey) {
      event.preventDefault();
      store.undo();
    } else if ((key === 'z' && event.shiftKey) || key === 'y') {
      event.preventDefault();
      store.redo();
    }
  };
  /** Buttons click on Space release, so the release is cancelled too or the key would do both. */
  const onKeyUp = (event: KeyboardEvent): void => {
    if (playback && event.key === ' ' && !spaceBelongsToTarget(event.target)) {
      event.preventDefault();
    }
  };
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('keyup', onKeyUp);
  return () => {
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('keyup', onKeyUp);
  };
}
